export type HearingLossThresholds = {
  floor: number;
  normal_max: number;
  slight_max: number;
  mild_max: number;
  moderate_max: number;
  moderately_severe_max: number;
  severe_max: number;
  ceiling: number;
};

export type HearingLossThresholdKey = keyof HearingLossThresholds;

export type HearingLossBand =
  | "normal"
  | "slight"
  | "mild"
  | "moderate"
  | "moderatelySevere"
  | "severe"
  | "profound";

export type HearingLossRange = {
  band: HearingLossBand;
  min: number;
  max: number;
  color: string;
  label: string;
};

// ASHA degree-of-hearing-loss scale (Clark 1981). Each `*_max` is the inclusive upper bound of its band.
// DEV-3238 added Slight (16-25 dB HL); before it, Normal ran to 25 dB HL.
export const DEFAULT_HEARING_LOSS_THRESHOLDS: HearingLossThresholds = {
  floor: -10,
  normal_max: 15,
  slight_max: 25,
  mild_max: 40,
  moderate_max: 55,
  moderately_severe_max: 70,
  severe_max: 90,
  ceiling: 120
};

const LEGACY_NORMAL_MAX = 25;

export const HEARING_LOSS_THRESHOLD_ORDER: HearingLossThresholdKey[] = [
  "floor",
  "normal_max",
  "slight_max",
  "mild_max",
  "moderate_max",
  "moderately_severe_max",
  "severe_max",
  "ceiling"
];

export const HEARING_LOSS_THRESHOLD_FIELDS: Array<{
  key: Exclude<HearingLossThresholdKey, "ceiling">;
  label: string;
}> = [
  { key: "floor", label: "Normal lower bound" },
  { key: "normal_max", label: "Normal upper cutoff" },
  { key: "slight_max", label: "Slight upper cutoff" },
  { key: "mild_max", label: "Mild upper cutoff" },
  { key: "moderate_max", label: "Moderate upper cutoff" },
  { key: "moderately_severe_max", label: "Moderately Severe upper cutoff" },
  { key: "severe_max", label: "Severe upper cutoff" }
];

export const HEARING_LOSS_BAND_COLORS: Record<HearingLossBand, string> = {
  normal: "rgba(34, 139, 34, 0.2)",
  slight: "rgba(96, 170, 20, 0.2)",
  mild: "rgba(144, 195, 0, 0.2)",
  moderate: "rgba(255, 165, 0, 0.2)",
  moderatelySevere: "rgba(255, 120, 0, 0.2)",
  severe: "rgba(255, 69, 0, 0.2)",
  profound: "rgba(178, 34, 34, 0.2)"
};

const toFiniteNumber = (value: unknown, fallback: number) => {
  if (value === null || value === undefined || value === "") return fallback;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

/**
 * Fill `slight_max` into thresholds saved before the Slight band existed (DEV-3238).
 *
 * Mirrors suno-backend `upgrade_legacy_hearing_loss_thresholds` (accounts/models/practice_settings.py),
 * which the `backfill_hearing_loss_slight_threshold` command also persists. Keep the three in step:
 * - Normal still at the old 25 dB default -> the ASHA defaults (Normal 15, Slight 25).
 * - A custom Normal below 25 dB with Mild above 25 dB -> Slight fills the gap up to 25 dB.
 * - Anything else -> Slight equals Normal, a hidden band, so nothing changes.
 */
export const upgradeLegacyHearingLossThresholds = (
  thresholds?: Partial<HearingLossThresholds> | null
): Partial<HearingLossThresholds> | null | undefined => {
  // Only a MISSING key is legacy: a present blank is a client error the validators must still reject.
  if (
    !thresholds ||
    Object.keys(thresholds).length === 0 ||
    "slight_max" in thresholds
  ) {
    return thresholds;
  }

  const asInteger = (key: HearingLossThresholdKey, fallback: number) => {
    if (!(key in thresholds)) return fallback;
    const value: unknown = thresholds[key];
    if (value === null || value === "" || typeof value === "boolean") {
      return null;
    }
    const numberValue = Number(value);
    return Number.isInteger(numberValue) ? numberValue : null;
  };
  const floor = asInteger("floor", -10);
  const normalMax = asInteger("normal_max", LEGACY_NORMAL_MAX);
  const mildMax = asInteger("mild_max", 40);
  if (floor === null || normalMax === null || mildMax === null) {
    return thresholds;
  }

  if (
    normalMax === LEGACY_NORMAL_MAX &&
    floor < DEFAULT_HEARING_LOSS_THRESHOLDS.normal_max
  ) {
    return {
      ...thresholds,
      normal_max: DEFAULT_HEARING_LOSS_THRESHOLDS.normal_max,
      slight_max: DEFAULT_HEARING_LOSS_THRESHOLDS.slight_max
    };
  }
  if (
    normalMax < DEFAULT_HEARING_LOSS_THRESHOLDS.slight_max &&
    DEFAULT_HEARING_LOSS_THRESHOLDS.slight_max < mildMax
  ) {
    return {
      ...thresholds,
      slight_max: DEFAULT_HEARING_LOSS_THRESHOLDS.slight_max
    };
  }
  return { ...thresholds, slight_max: normalMax };
};

export const normalizeHearingLossThresholds = (
  thresholds?: Partial<HearingLossThresholds> | null
): HearingLossThresholds => {
  const upgraded = upgradeLegacyHearingLossThresholds(thresholds);
  return HEARING_LOSS_THRESHOLD_ORDER.reduce(
    (normalized, key) => ({
      ...normalized,
      [key]: toFiniteNumber(
        upgraded?.[key],
        DEFAULT_HEARING_LOSS_THRESHOLDS[key]
      )
    }),
    {} as HearingLossThresholds
  );
};

/** A Slight cutoff equal to the Normal cutoff is how a practice turns the Slight band off. */
export const hasSlightBand = (thresholds: HearingLossThresholds) =>
  thresholds.slight_max > thresholds.normal_max;

export const validateHearingLossThresholds = (
  thresholds?: Partial<Record<HearingLossThresholdKey, unknown>> | null
): true | string => {
  const valueOf = (key: HearingLossThresholdKey) => {
    const value = thresholds?.[key];
    return value === null || value === undefined || value === ""
      ? Number.NaN
      : Number(value);
  };
  const values = HEARING_LOSS_THRESHOLD_ORDER.map(valueOf);

  if (values.some((value) => !Number.isFinite(value))) {
    return "All hearing-loss thresholds are required";
  }

  if (values[0] < -20 || values[values.length - 1] > 130) {
    return "Hearing-loss thresholds must stay within -20 to 130 dB HL";
  }

  const normalMax = valueOf("normal_max");
  const slightMax = valueOf("slight_max");
  if (slightMax < normalMax) {
    return "The Slight cutoff cannot be below the Normal cutoff";
  }

  // A hidden (zero-width) Slight band drops out of the order check; every other band must be non-empty.
  const ordered =
    slightMax > normalMax
      ? values
      : values.filter(
          (_, index) => HEARING_LOSS_THRESHOLD_ORDER[index] !== "slight_max"
        );
  for (let i = 1; i < ordered.length; i += 1) {
    if (ordered[i] <= ordered[i - 1]) {
      return "Each hearing-loss threshold must be greater than the previous one";
    }
  }

  return true;
};

/** Every band, in order, including a zero-width Slight band. The settings screen needs all of them. */
export const buildAllHearingLossRanges = (
  thresholds?: Partial<HearingLossThresholds> | null
): HearingLossRange[] => {
  const normalized = normalizeHearingLossThresholds(thresholds);
  const range = (
    band: HearingLossBand,
    label: string,
    min: number,
    max: number
  ): HearingLossRange => ({
    band,
    min,
    max,
    label,
    color: HEARING_LOSS_BAND_COLORS[band]
  });

  return [
    range("normal", "Normal", normalized.floor, normalized.normal_max),
    range("slight", "Slight", normalized.normal_max, normalized.slight_max),
    range("mild", "Mild", normalized.slight_max, normalized.mild_max),
    range("moderate", "Moderate", normalized.mild_max, normalized.moderate_max),
    range(
      "moderatelySevere",
      "Moderately Severe",
      normalized.moderate_max,
      normalized.moderately_severe_max
    ),
    range(
      "severe",
      "Severe",
      normalized.moderately_severe_max,
      normalized.severe_max
    ),
    range("profound", "Profound", normalized.severe_max, normalized.ceiling)
  ];
};

/** The bands an audiogram overlay draws: a hidden Slight band is left out, so it gets no label. */
export const buildHearingLossRanges = (
  thresholds?: Partial<HearingLossThresholds> | null
): HearingLossRange[] =>
  buildAllHearingLossRanges(thresholds).filter(
    (range) => range.max > range.min
  );
